/**
 * Everpret RewardSynchronizer
 *
 * The reward *decision* (which tiers are reached, which gift lines should
 * exist) is computed server-side in snippets/reward-engine.liquid on every
 * render, using the same cart + campaign data Shopify already has — so the
 * decision logic exists in exactly one place, not duplicated in JS. This
 * module only executes that decision against the real Shopify cart:
 *
 *   read <script data-reward-state> (fresh on every section re-render)
 *     -> diff against actual cart line items (matched by the `_reward_id`
 *        line-item property, never by product/variant id alone)
 *     -> batch all removals into one /cart/update.js call
 *     -> batch all additions into one /cart/add.js call
 *
 * Idempotent by construction: if the desired state already matches the cart,
 * both batches are empty and nothing is sent. A promise-based lock prevents
 * overlapping syncs (e.g. two cart:change events firing close together), and
 * is always released, including on error, so a failed sync never wedges the
 * cart. A configured gift that's out of stock/unavailable is skipped with a
 * console warning, not retried, and never blocks the rest of the cart.
 */
(function (window) {
  'use strict';

  function readState(root) {
    var node = (root || document).querySelector('[data-reward-state]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      console.warn('[Everpret RewardSynchronizer] could not parse reward state', e);
      return null;
    }
  }

  function findLineKeysByRewardId(cart, rewardId) {
    return (cart.items || [])
      .filter(function (line) {
        return line.properties && line.properties._reward_id === rewardId;
      })
      .map(function (line) {
        return line.key;
      });
  }

  function RewardSynchronizer(cartStore) {
    this.cartStore = cartStore;
    this._lock = null;
  }

  RewardSynchronizer.prototype.sync = function (root) {
    if (this._lock) return this._lock;

    var state = readState(root);
    if (!state) return Promise.resolve(null);

    var toAdd = (state.rewardsToAdd || []).filter(function (r) {
      return r.variantId && r.available !== false;
    });
    var toRemove = state.rewardsToRemove || [];

    if (!toAdd.length && !toRemove.length) {
      return Promise.resolve(state.cart || null);
    }

    var cartStore = this.cartStore;
    var self = this;

    // The cart used to look up existing gift-line keys comes from CartStore's
    // in-memory state, not an extra GET /cart.js. It's already current by the
    // time sync() runs: CartStore's own 'cart:change' listener (which sets
    // this.cart) is registered at script load, before cart-drawer.js's
    // listener that calls sync(), so it always updates first for the same
    // event. Skipping this fetch removes one full round-trip from every
    // reward add/remove. Falls back to a live fetch only if sync() somehow
    // runs before any cart has ever been fetched (practically unreachable,
    // since the page's own bootstrap always fetches the cart first).
    var cartPromise = cartStore.cart ? Promise.resolve(cartStore.cart) : cartStore.fetchCart();

    this._lock = cartPromise
      .then(function (cart) {
        var removeKeys = [];
        toRemove.forEach(function (r) {
          removeKeys = removeKeys.concat(findLineKeysByRewardId(cart, r.rewardId));
        });

        var chain = Promise.resolve();

        if (removeKeys.length) {
          var updates = {};
          removeKeys.forEach(function (key) {
            updates[key] = 0;
          });
          chain = chain.then(function () {
            return cartStore.updateItems(updates, { baseEvent: 'reward:remove' });
          });
        }

        if (toAdd.length) {
          var addPayload = toAdd.map(function (r) {
            return {
              id: r.variantId,
              quantity: r.quantity || 1,
              properties: {
                _reward_gift: 'true',
                _reward_id: r.rewardId,
                _reward_campaign: state.campaign || '',
              },
            };
          });
          chain = chain.then(function () {
            return cartStore.addItems(addPayload, { baseEvent: 'reward:add' });
          });
        }

        return chain;
      })
      .catch(function (err) {
        console.warn('[Everpret RewardSynchronizer] sync failed, cart left as-is', err);
      })
      .finally(function () {
        self._lock = null;
      });

    return this._lock;
  };

  window.Everpret = window.Everpret || {};
  window.Everpret.RewardSynchronizer = RewardSynchronizer;
})(window);
